import React from "react";
import { useSelector } from "react-redux";
import css from "./index.module.scss";
import classNames from "classnames";
import { closestCenter, DndContext, DragOverlay, UniqueIdentifier } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { SortableListProvider, useSortableListContainer } from "..";
import { useCallback } from "react";
import { find, isFunction } from "lodash";
import { SortableItem } from "../Item";
import AddToContent from "@uiview/views/AISaas/AddToContent";
import useFeatureTags from "@custom-hooks/useFeatureTags";
import { AYLWAYS_SHOW_OUTPUT, WITHOUT_INPUT_ID } from "@uiview/views/AISaas/AddToContent/conts";
import { useHomeUsePluginContainer } from "@custom-hooks/HomeUsePluginRoot";

function SortableListInner<T = any>(props: SortableList.Props<T>) {
  const {
    sortableOptions,
    listType,
    isShare,
    activeId,
    sensors,
    onDragStart,
    onDragEnd,
    modifiers,
    disabled,
    sortableListRef,
  } = useSortableListContainer();

  const { showAddClickHere } = useFeatureTags();

  const { className, renderItemChildren, renderActiveChildren } = props;
  const {
    saasAndThinkState: { saasUIData },
  } = useHomeUsePluginContainer();

  const _renderSortableItem = useCallback(
    (item: SortableList.SortableData<T>, index: number, array: any) => {
      const nextId =
        array[index + 1]?.id ||
        (listType === "input" ? WITHOUT_INPUT_ID : "") ||
        (listType === "output" ? AYLWAYS_SHOW_OUTPUT : "");
      //  input在staticMode下不显示add content
      const hideAdd = listType === "input" && saasUIData.staticMode;
      const isShowAdd = showAddClickHere && !isShare && !hideAdd;
      return (
        <React.Fragment key={item.id}>
          {isShowAdd && <AddToContent id={item.id} nextId={nextId} type={listType} />}
          <SortableItem value={item} index={index} type={listType} renderItemChildren={renderItemChildren} />
        </React.Fragment>
      );
    },
    [listType, renderItemChildren]
  );

  const _renderActiveChildren = useCallback(
    (id: UniqueIdentifier) => {
      const target = find(sortableOptions, ["id", id]);
      if (target && renderActiveChildren) {
        return renderActiveChildren(target);
      }
      return null;
    },
    [renderActiveChildren]
  );

  return (
    <DndContext
      sensors={sensors}
      modifiers={modifiers}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      autoScroll
    >
      <div ref={sortableListRef} className={classNames(css["SortableList"], className)}>
        <SortableContext disabled={disabled} items={sortableOptions} strategy={verticalListSortingStrategy}>
          {sortableOptions.map(_renderSortableItem)}
        </SortableContext>

        {activeId && isFunction(renderActiveChildren) ? (
          <DragOverlay>{_renderActiveChildren(activeId)}</DragOverlay>
        ) : null}
      </div>
    </DndContext>
  );
}

export default function SortableList<T>(props: SortableList.Props<T>) {
  return (
    <SortableListProvider initialState={props}>
      <SortableListInner<T> {...props} />
    </SortableListProvider>
  );
}
